// Onboarding wizard — CANONICAL initial state.
//
// Extracted 2026-08-17 for IDI-62 (318_ §3.1). Revised 2026-08-17 after
// 320_'s Case B ruling: this file must contain EVERY value the wizard's
// state can hold on a no-change walk-through, not just the values the
// top-level useState declares.
//
// Rule (AGENTS.md §9 candidate):
//
//   A wizard step's useState default IS part of the form's write contract.
//   A default declared inside a step, seeded from `data.x || DEFAULT`, and
//   committed on Next becomes indistinguishable from a user's choice the
//   moment they click through. The canonical initial state must contain
//   EVERY default the flow can commit, not just the ones the top-level
//   component declares.
//
// Both the wizard component AND buildSettingsPayload import from here.
// Step 3 (MarginsLabor) imports DEFAULT_CATEGORIES / DEFAULT_LABOR_RATES
// so that `data.categories || DEFAULT_CATEGORIES` becomes inert — the
// wizardData already carries the same reference-equal defaults, so the
// no-change Next commits the same value that was already there.
//
// If you add a new step and give it its own local default that gets
// committed on Next, move that default here first. See §5 of 320_ for
// the reasoning.

// Step 3 defaults — the six themes and the flat labor-rates shape.
// §1 (2026-08-07): labor_rates shape MUST equal Settings' shape so the
// wizard's write and Settings' read land at the same key.
// IDI-397 D5: Settings' six, in Settings' order and keys (MarginsPage.js
// SEEDED_ITEM_CATEGORIES — "bats", not "batts").
export const DEFAULT_CATEGORIES = Object.freeze([
  Object.freeze({ id: 'spray_foam', name: 'Spray Foam', margin: null, isCustom: false }),
  Object.freeze({ id: 'fiberglass', name: 'Fiberglass', margin: null, isCustom: false }),
  Object.freeze({ id: 'bats', name: 'Batts', margin: null, isCustom: false }),
  Object.freeze({ id: 'flow', name: 'Flow', margin: null, isCustom: false }),
  Object.freeze({ id: 'accessories', name: 'Accessories', margin: null, isCustom: false }),
  Object.freeze({ id: 'cellulose', name: 'Cellulose', margin: null, isCustom: false }),
]);

export const DEFAULT_LABOR_RATES = Object.freeze({
  piece: Object.freeze({ sq_ft: '', linear_ft: '' }),
  set: '', // IDI-834 D2: Settings' Per set rate
  hourly: '',
  day: '',
});

export const INITIAL_WIZARD_DATA = Object.freeze({
  // Step 1 — About You
  firstName: '',
  lastName: '',
  email: '', // read-only from claim
  phone: '',
  // Step 2 — Your Business
  companyName: '',
  businessPhone: '',
  businessEmail: '',
  subdomain: '',
  brandColor: '#D70321',
  // IDI-656 D7: Settings → Branding's defaults.
  secondaryColor: '#4A4A4A',
  textColor: '#1F1F1F',
  // IDI-656 D1: Branding's logo_url and logo_meta.
  logoUrl: '',
  logoMeta: null,
  logoPosition: 'left',
  website: '',
  emailSignature: '',
  includeLogoInSignature: false,
  roundLineItems: false,
  roundQuoteTotal: false,
  address: Object.freeze({ street: '', city: '', state: '', zip: '' }),
  // Step 3 — Margins & Labor
  //   NOTE: was `null` in v2. That is the state at MOUNT of the wizard, not
  //   the state at handleComplete — Step 3's own useState seeds from
  //   DEFAULT_CATEGORIES / DEFAULT_LABOR_RATES when `data.categories == null`,
  //   and commits back to wizardData on Next. So on a no-change walk-through,
  //   wizardData.categories arrives at handleComplete === DEFAULT_CATEGORIES.
  //   Putting the defaults here (and importing them into Step 3) makes the
  //   commit a no-op.
  categories: DEFAULT_CATEGORIES,
  masterDefaultMargin: 5, // §30.5: BIDIT seeds 5%, surfaced for adjustment
  laborRates: DEFAULT_LABOR_RATES,
  // Step 4 — Tax & Defaults
  //   IDI-655: storedTaxRates is Settings' Tax & Rates rows (display only,
  //   never saved as progress); taxRateEdits holds only the dealer's typed
  //   changes — stored rates edited by id, and added {state, rate} rows.
  storedTaxRates: Object.freeze([]),
  taxRateEdits: Object.freeze({ edits: Object.freeze({}), added: Object.freeze([]) }),
  //   IDI-626: termsAndConditions is Settings' T&C Templates entries (display
  //   only); tcChanges holds setup's adds, edits (by id) and deletes.
  termsAndConditions: Object.freeze([]),
  tcChanges: Object.freeze({ added: Object.freeze([]), edits: Object.freeze({}), deleted: Object.freeze([]) }),
  //   IDI-834 D4: only the Quote Defaults fields setup changed (strings, as
  //   Quote Defaults' Save sends them), kept in their own object so the
  //   step's progress never carries a top-level price_rounding/labor_burden_pct.
  quoteDefaultChanges: Object.freeze({}),
  // Step 5 — Invite Team
  invitees: Object.freeze([]),
  // Step 6 — Crews. IDI-657: storedCrews is Settings' crews (display only,
  //   never saved as progress); crewAdds holds setup's new crews, each with
  //   its add-time text id, written through add_list_item at the finish.
  storedCrews: Object.freeze([]),
  crewAdds: Object.freeze([]),
  // Step 7 — Resources. IDI-657: resourceAdds holds setup's new Resource
  //   Types rows; Settings' rows are read from Resource Types' list copy.
  resourceAdds: Object.freeze([]),
  // Step 8 — Customers
  customers: Object.freeze([]),
});
